// ─────────────────────────────────────────────────────────────────────────────
// Automation Insights — Platform Summary Panel
//
// Same feature set as the standalone repo's
// components/platform-summary/PlatformSummaryPanel.tsx: source-filter toggle
// (All/Automation/Tori/Human), ALL/INC/RITM tabs, a "Table Settings" picker
// for column visibility + count-vs-percentage display modes (persisted to
// localStorage under the same versioned key/shape), an Excel export button,
// and a sticky-header/sticky-first-column table with a grand-total row.
//
// Scope note (checked against the live component before building, not
// assumed): the old file also imports DrilldownModal/PivotTable elsewhere in
// the repo, but this panel — the one actually rendered on the live
// standalone site — has no row-click/drilldown wiring at all. Confirmed by
// reading the full 1,524-line file. Drilldown is therefore NOT part of this
// port; it moves to Phase 10 with the other not-currently-live pieces,
// rather than being silently built here on a guess.
//
// Implementation substitution (framework change, not a feature change): the
// old column-settings overlay was a hand-rolled fixed-position portal with
// manual viewport math, focus trap, and escape/click-outside handling. MUI's
// Popover does all of that natively, so the same "click a button, get an
// anchored settings panel" feature is rebuilt with less code.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControlLabel,
  IconButton,
  Popover,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { PLATFORM_LABEL_BY_KEY } from '../../config/automationPlatforms';
import { buildTimeFilterRequestParams, useAutomationFilters } from '../../contexts/AutomationFilterContext';
import { useAssignmentGroupScope } from '../../contexts/AssignmentGroupScopeContext';
import {
  AutomationBasis,
  PlatformSummaryData,
  PlatformSummaryMetrics,
  PlatformSummaryParams,
  PlatformTableGroupRow,
  SourceFilterType,
  fetchAutomationTicketDrilldown,
  fetchPlatformSummary,
  fetchPlatformTableGroups,
} from '../../services/automationInsightsService';
import { exportPlatformSummaryToExcel, MetricPresentation } from '../../utils/exportPlatformSummaryExcel';
import TicketDrilldownModal, { DrilldownContext } from '../common/TicketDrilldownModal';

const AUTOMATION_CLICKABLE_COLUMNS = new Set([
  'vol_created', 'vol_resolved', 'auto_fulfilled', 'tori_vol',
  'cne_count', 'reopen_count', 'open_tickets', 'l1_resolved', 'overall_resolved',
]);

type TicketType = 'ALL' | 'INC' | 'RITM';
type MetricKey = keyof PlatformSummaryMetrics;
type MetricCategory = 'volumes' | 'operations' | 'performance';
type DisplayMode = 'count' | 'percentage';
type DisplayModeMetric = 'vol_resolved' | 'auto_fulfilled' | 'cne_count' | 'reopen_count';
type OptionalColumnId = Exclude<MetricKey, never>;

interface DisplayModeConfig {
  metric: DisplayModeMetric;
  defaultMode: DisplayMode;
}

interface ColumnConfig {
  id: OptionalColumnId;
  label: string;
  category: MetricCategory;
  displayModeConfig?: DisplayModeConfig;
  // Overall/L1 Resolved + Resolution % only make sense for the Resolved
  // View (RESOLVED_DATE_MIGRATION_PLAN.md §2.4/§7) — a ticket's SLA history
  // and current assignment_group are evaluated as of its resolution, which
  // the Created View's cohort (by created_date) doesn't define. Columns
  // flagged here are hidden from visibleColumns whenever selectedBasis
  // !== 'resolved', regardless of the user's saved column-visibility prefs.
  resolvedOnly?: boolean;
  // Mirror of resolvedOnly for the other direction: Volume Created / Volume
  // Resolved are the Created-View's own cohort counters and read as
  // confusingly redundant once the Resolved View already shows L1/Overall/
  // Resolution % for that same cohort — hidden whenever selectedBasis ===
  // 'resolved', regardless of saved prefs. Added 2026-10 per user request.
  createdOnly?: boolean;
}

const PLATFORM_SEGMENTS: Array<{ label: string; value: SourceFilterType }> = [
  { label: 'All', value: 'all' },
  { label: 'Automation', value: 'automation' },
  { label: 'Tori', value: 'tori' },
  { label: 'Human', value: 'human' },
];

const METRIC_CATEGORY_LABELS: Record<MetricCategory, string> = {
  volumes: 'Volumes',
  operations: 'Operations',
  performance: 'Performance',
};

const COLUMNS: ColumnConfig[] = [
  { id: 'vol_created', label: 'Volume Created', category: 'volumes', createdOnly: true },
  {
    id: 'vol_resolved',
    label: 'Volume Resolved',
    category: 'volumes',
    displayModeConfig: { metric: 'vol_resolved', defaultMode: 'count' },
    createdOnly: true,
  },
  {
    id: 'auto_fulfilled',
    label: 'Auto Fulfilled',
    category: 'volumes',
    displayModeConfig: { metric: 'auto_fulfilled', defaultMode: 'count' },
  },
  { id: 'tori_vol', label: 'TORI Volume', category: 'operations' },
  // All-time, basis/date-range-independent — see PlatformSummaryMetrics'
  // open_tickets doc comment. Populated on both top-level platform rows and
  // assignment-group drill-down rows (added 2026-10).
  { id: 'open_tickets', label: 'Open Tickets', category: 'volumes' },
  // Resolved View only (RESOLVED_DATE_MIGRATION_PLAN.md §2.4/§5.3, added
  // 2026-10). L1 Resolved = resolved while the ticket's current/final
  // assignment_group was one of our own tracked queues. Overall Resolved =
  // resolved after its SLA history touched one of our tracked queues at any
  // point, regardless of who actually closed it (always >= L1 Resolved).
  // Resolution % = L1 / Overall.
  { id: 'l1_resolved', label: 'L1 Resolved', category: 'volumes', resolvedOnly: true },
  { id: 'overall_resolved', label: 'Overall Resolved', category: 'volumes', resolvedOnly: true },
  { id: 'resolution_pct', label: 'Resolution %', category: 'performance', resolvedOnly: true },
  {
    id: 'cne_count',
    label: 'CNE Count',
    category: 'operations',
    displayModeConfig: { metric: 'cne_count', defaultMode: 'count' },
  },
  {
    id: 'reopen_count',
    label: 'Reopen Count',
    category: 'operations',
    displayModeConfig: { metric: 'reopen_count', defaultMode: 'count' },
  },
  { id: 'avg_mttr', label: 'Avg MTTR (hours)', category: 'performance' },
  { id: 'avg_mttr_p50', label: 'P50', category: 'performance' },
  { id: 'avg_mttr_p80', label: 'P80', category: 'performance' },
  { id: 'avg_mttr_p85', label: 'P85', category: 'performance' },
  { id: 'avg_mttr_p90', label: 'P90', category: 'performance' },
];

const DEFAULT_VISIBILITY: Record<OptionalColumnId, boolean> = COLUMNS.reduce((acc, c) => {
  acc[c.id] = true;
  return acc;
}, {} as Record<OptionalColumnId, boolean>);

// Smart per-view defaults (added 2026-10, confirmed with the user) — only
// ever applied automatically when the user hasn't customized Table Settings
// yet (see hasCustomizedColumns below); anyone who's touched that picker
// keeps their own saved choice exactly as before, unaffected by this.
// Volume Created/Volume Resolved are the Created-View's own cohort
// counters — confusing once the Resolved View already shows Volume
// Handled/L1 Resolved/Resolution % for that same cohort (createdOnly on the
// column config already hides them outright in Resolved View regardless of
// this map; listed here too for clarity). TORI Volume reads as near-always-
// zero noise under the Automation/Human tabs (it's a human-vs-bot-vs-TORI
// split, not relevant once already filtered to one of those slices); Auto
// Fulfilled is the mirror case for the Tori/Human tabs.
function getSmartDefaultVisibility(
  basis: AutomationBasis,
  sourceFilter: SourceFilterType,
): Record<OptionalColumnId, boolean> {
  const visibility = { ...DEFAULT_VISIBILITY };
  // Driven by each column's own resolvedOnly/createdOnly flag, so a new
  // column tagged one way or the other automatically gets the right
  // default without a second hardcoded list to keep in sync.
  COLUMNS.forEach((c) => {
    if (c.resolvedOnly && basis !== 'resolved') visibility[c.id] = false;
    if (c.createdOnly && basis !== 'created') visibility[c.id] = false;
  });
  if (sourceFilter === 'automation' || sourceFilter === 'human') {
    visibility.tori_vol = false;
  }
  if (sourceFilter === 'tori' || sourceFilter === 'human') {
    visibility.auto_fulfilled = false;
  }
  return visibility;
}

const DEFAULT_DISPLAY_MODES: Record<DisplayModeMetric, DisplayMode> = {
  vol_resolved: 'count',
  auto_fulfilled: 'count',
  cne_count: 'count',
  reopen_count: 'count',
};

const STORAGE_KEY = 'platform-summary:column-visibility:v1';
const STORAGE_VERSION = 1;

// Display-only label for ticket type — the underlying value stays "RITM"
// everywhere (API contract, SNOW enum, query params). Only what's shown on
// screen/export reads "SR". Do not use this for comparisons.
const TICKET_TYPE_DISPLAY_LABEL: Record<TicketType, string> = {
  ALL: 'ALL',
  INC: 'INC',
  RITM: 'SR',
};

function formatValue(value: number | undefined, key: MetricKey): string {
  if (value === undefined || value === null) return 'N/A';
  if (key.includes('pct')) return `${value.toFixed(1)}%`;
  if (key.includes('mttr')) return `${value.toFixed(1)}h`;
  return value.toLocaleString();
}

function formatDayRange(fromDate: string, toDate: string): string {
  try {
    return `${format(parseISO(fromDate), 'MMM d, yyyy')} - ${format(parseISO(toDate), 'MMM d, yyyy')}`;
  } catch {
    return `${fromDate} - ${toDate}`;
  }
}

function formatSelectedWeeks(periods: string[]): string {
  if (periods.length === 0) return 'No weeks selected';
  return periods
    .map((p) => {
      const m = /^(\d{4})-W(\d{2})$/.exec(p);
      return m ? `Week ${m[2]}` : p;
    })
    .join(', ');
}

function formatSelectedMonths(periods: string[]): string {
  if (periods.length === 0) return 'No months selected';
  return periods
    .map((p) => {
      const m = /^(\d{4})-(\d{2})$/.exec(p);
      if (!m) return p;
      try {
        return format(parseISO(`${m[1]}-${m[2]}-01`), 'MMMM yyyy');
      } catch {
        return `${m[1]}-${m[2]}`;
      }
    })
    .join(', ');
}

export default function PlatformSummaryPanel() {
  const { filters } = useAutomationFilters();
  const { scope, setScope, clearScope } = useAssignmentGroupScope();

  const [selectedSourceFilter, setSelectedSourceFilter] = useState<SourceFilterType>('all');
  const [selectedTicketType, setSelectedTicketType] = useState<TicketType>('ALL');
  // 'created': every number reflects tickets CREATED in the selected
  // week(s) — the existing/default view. 'resolved': reflects tickets
  // RESOLVED in that week instead — a different cohort, not a recompute of
  // the same one. Fully independent of every other control on this table
  // (source filter, ticket type, Table Settings) — confirmed with the user.
  // Added 2026-10, RESOLVED_DATE_MIGRATION_PLAN.md §7.
  const [selectedBasis, setSelectedBasis] = useState<AutomationBasis>('created');
  const [columnVisibility, setColumnVisibility] = useState<Record<OptionalColumnId, boolean>>(
    getSmartDefaultVisibility('created', 'all'),
  );
  const [displayModes, setDisplayModes] = useState<Record<DisplayModeMetric, DisplayMode>>(DEFAULT_DISPLAY_MODES);
  // True once the user has an explicit saved preference (restored from
  // localStorage) or has touched Table Settings this session — from then on
  // the smart per-view defaults below stop auto-applying, so a manual choice
  // is never silently overwritten by switching tabs/basis.
  const [hasCustomizedColumns, setHasCustomizedColumns] = useState(false);
  const [settingsAnchorEl, setSettingsAnchorEl] = useState<HTMLElement | null>(null);

  const [data, setData] = useState<PlatformSummaryData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exportMessage, setExportMessage] = useState('');
  const [isExporting, setIsExporting] = useState(false);

  // Assignment-group drill-down (Stage 4, Platform View) — one platform
  // expanded at a time, accordion-style. Expanding a row broadcasts that
  // platform via AssignmentGroupScopeContext for Platform Performance below
  // to pick up; clicking one of the resulting sub-rows further narrows the
  // scope to that specific real group.
  const [expandedPlatformKey, setExpandedPlatformKey] = useState<string | null>(null);
  const [groupRows, setGroupRows] = useState<PlatformTableGroupRow[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsError, setGroupsError] = useState<string | null>(null);

  const [drilldownCtx, setDrilldownCtx] = useState<DrilldownContext | null>(null);

  const openAutomationDrilldown = useCallback((
    platformKey: string,
    platformLabel: string,
    columnId: string,
    columnLabel: string,
    assignmentGroup?: string,
  ) => {
    setDrilldownCtx({
      title: `${platformLabel} — ${columnLabel}${assignmentGroup && assignmentGroup !== '__OTHER__' ? ` (${assignmentGroup})` : assignmentGroup === '__OTHER__' ? ' (Other Groups)' : ''}`,
      showAutomationCols: true,
      fetchPage: (page) => fetchAutomationTicketDrilldown({
        platform: platformKey,
        column: columnId,
        basis: selectedBasis,
        ticketType: selectedTicketType,
        sourceFilter: selectedSourceFilter,
        assignmentGroup,
        ...buildTimeFilterRequestParams(filters),
        page,
      }),
    });
  }, [filters, selectedBasis, selectedTicketType, selectedSourceFilter]);

  // Restore persisted column settings (same storage key/shape as the old panel)
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed || parsed.version !== STORAGE_VERSION) return;

      const visibility = COLUMNS.reduce((acc, c) => {
        const v = parsed.visibility?.[c.id];
        acc[c.id] = typeof v === 'boolean' ? v : true;
        return acc;
      }, {} as Record<OptionalColumnId, boolean>);

      const modes = (Object.keys(DEFAULT_DISPLAY_MODES) as DisplayModeMetric[]).reduce((acc, m) => {
        const v = parsed.displayModes?.[m];
        acc[m] = v === 'percentage' || v === 'count' ? v : DEFAULT_DISPLAY_MODES[m];
        return acc;
      }, {} as Record<DisplayModeMetric, DisplayMode>);

      setColumnVisibility(visibility);
      setDisplayModes(modes);
      setHasCustomizedColumns(true);
    } catch {
      // ignore corrupted storage
    }
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: STORAGE_VERSION, visibility: columnVisibility, displayModes }));
    } catch {
      // ignore storage permission failures
    }
  }, [columnVisibility, displayModes]);

  // Smart per-view defaults — only auto-apply while the user hasn't
  // customized Table Settings (see hasCustomizedColumns doc comment above).
  useEffect(() => {
    if (hasCustomizedColumns) return;
    setColumnVisibility(getSmartDefaultVisibility(selectedBasis, selectedSourceFilter));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBasis, selectedSourceFilter, hasCustomizedColumns]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const timeParams = buildTimeFilterRequestParams(filters);
    const params: PlatformSummaryParams = {
      platform: 'all',
      ticketType: selectedTicketType,
      sourceFilter: selectedSourceFilter,
      timeFilterType: timeParams.timeFilterType,
      selectedPeriods: timeParams.selectedPeriods,
      dateFrom: timeParams.dateFrom,
      dateTo: timeParams.dateTo,
      basis: selectedBasis,
    };

    fetchPlatformSummary(params)
      .then((response) => {
        if (cancelled) return;
        setData(response);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Failed to load platform summary');
        setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedSourceFilter, selectedTicketType, selectedBasis, filters]);

  // Fetch the real assignment-group breakdown for whichever platform row is
  // currently expanded. Collapsing (expandedPlatformKey -> null) just clears
  // the rows rather than fetching anything.
  useEffect(() => {
    if (!expandedPlatformKey) {
      setGroupRows([]);
      setGroupsError(null);
      return;
    }

    let cancelled = false;
    setGroupsLoading(true);
    setGroupsError(null);

    const timeParams = buildTimeFilterRequestParams(filters);

    fetchPlatformTableGroups({
      platform: expandedPlatformKey,
      ticketType: selectedTicketType,
      sourceFilter: selectedSourceFilter,
      timeFilterType: timeParams.timeFilterType,
      selectedPeriods: timeParams.selectedPeriods,
      dateFrom: timeParams.dateFrom,
      dateTo: timeParams.dateTo,
      basis: selectedBasis,
    })
      .then((response) => {
        if (cancelled) return;
        setGroupRows(response.rows ?? []);
      })
      .catch((err) => {
        if (cancelled) return;
        setGroupsError(err?.message || 'Failed to load assignment groups');
        setGroupRows([]);
      })
      .finally(() => {
        if (!cancelled) setGroupsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [expandedPlatformKey, selectedTicketType, selectedSourceFilter, selectedBasis, filters]);

  const tableRows = useMemo(() => data?.rows ?? [], [data]);
  const hasRows = tableRows.length > 0;
  const showTicketTypeColumn = selectedTicketType === 'ALL';

  const visibleColumns = useMemo(
    () =>
      COLUMNS
        // columnVisibility (seeded by getSmartDefaultVisibility, but fully
        // user-overridable via Table Settings — see hasCustomizedColumns) is
        // the ONLY source of truth for what's shown. resolvedOnly/createdOnly
        // are hints used solely to compute the per-view DEFAULT below; they
        // must never also hard-block a column here, or checking a box in
        // Table Settings would silently do nothing — exactly the bug the
        // user reported 2026-10 (checking Volume Created/Resolved in the
        // Resolved View had no effect).
        .filter((c) => columnVisibility[c.id])
        // "Overall Resolved" reads as "Volume Handled" in the Resolved View —
        // same underlying metric (resolved_date-based, from
        // automation_sla_platform_matches), just a clearer label once Volume
        // Created/Resolved (the Created-View cohort counters) are hidden
        // alongside it. Label only changes for display; the column id/data
        // key is untouched. Added 2026-10 per user request.
        .map((c) =>
          c.id === 'overall_resolved' && selectedBasis === 'resolved'
            ? { ...c, label: 'Volume Handled' }
            : c,
        ),
    [columnVisibility, selectedBasis],
  );
  const columnGroups = useMemo(() => {
    const grouped = new Map<MetricCategory, ColumnConfig[]>();
    COLUMNS.forEach((c) => {
      const bucket = grouped.get(c.category);
      if (bucket) bucket.push(c);
      else grouped.set(c.category, [c]);
    });
    return Array.from(grouped.entries()).map(([category, columns]) => ({ category, label: METRIC_CATEGORY_LABELS[category], columns }));
  }, []);

  const getMetricPresentation = useCallback(
    (columnId: string, value: number | undefined, row: PlatformSummaryMetrics): MetricPresentation => {
      const metricKey = columnId as MetricKey;

      if (metricKey === 'vol_resolved' && displayModes.vol_resolved === 'percentage') {
        const denominator = row.vol_created;
        if (!denominator || value === undefined || value === null) return { text: 'N/A', excelValue: 'N/A' };
        const ratio = (value / denominator) * 100;
        return { text: `${ratio.toFixed(1)}%`, excelValue: ratio / 100, excelNumFmt: '0.0%' };
      }

      if (metricKey === 'auto_fulfilled' && displayModes.auto_fulfilled === 'percentage') {
        const denominator = row.vol_created;
        if (!denominator) return { text: 'N/A', excelValue: 'N/A' };
        const ratio = ((value ?? 0) / denominator) * 100;
        return { text: `${ratio.toFixed(1)}%`, excelValue: ratio / 100, excelNumFmt: '0.0%' };
      }

      if (
        (metricKey === 'cne_count' && displayModes.cne_count === 'percentage') ||
        (metricKey === 'reopen_count' && displayModes.reopen_count === 'percentage')
      ) {
        // % of vol_created — same convention as auto_fulfilled's percentage
        // mode, and same convention the backend uses for cne_pct/reopen_pct
        // (kept computed client-side here, matching this table's existing
        // toggle pattern, rather than switching to the backend field).
        const denominator = row.vol_created;
        if (!denominator) return { text: 'N/A', excelValue: 'N/A' };
        const ratio = ((value ?? 0) / denominator) * 100;
        return { text: `${ratio.toFixed(1)}%`, excelValue: ratio / 100, excelNumFmt: '0.0%' };
      }

      const text = formatValue(value, metricKey);
      if (value === undefined || value === null) return { text, excelValue: text };
      if (metricKey.includes('mttr')) return { text, excelValue: value, excelNumFmt: '0.0"h"' };
      return { text, excelValue: value, excelNumFmt: '#,##0' };
    },
    [displayModes]
  );

  const getMetricText = useCallback(
    (columnId: string, value: number | undefined, row: PlatformSummaryMetrics) => getMetricPresentation(columnId, value, row).text,
    [getMetricPresentation]
  );

  const handleToggleExpand = (platformKey: string) => {
    if (expandedPlatformKey === platformKey) {
      setExpandedPlatformKey(null);
      clearScope();
    } else {
      setExpandedPlatformKey(platformKey);
      setScope({ platform: platformKey, assignmentGroup: null });
    }
  };

  const handleSelectGroup = (platformKey: string, group: string) => {
    if (scope.platform === platformKey && scope.assignmentGroup === group) {
      setScope({ platform: platformKey, assignmentGroup: null });
    } else {
      setScope({ platform: platformKey, assignmentGroup: group });
    }
  };

  const updateColumnVisibility = (id: OptionalColumnId, checked: boolean) => {
    setHasCustomizedColumns(true);
    setColumnVisibility((prev) => ({ ...prev, [id]: checked }));
  };
  const updateDisplayMode = (metric: DisplayModeMetric, mode: DisplayMode) => setDisplayModes((prev) => ({ ...prev, [metric]: mode }));
  const resetTableSettings = () => {
    // Back to the smart per-view defaults for whatever view is active right
    // now, and re-enable auto-switching those defaults as basis/source
    // filter change going forward (same as a first-ever visit).
    setHasCustomizedColumns(false);
    setColumnVisibility(getSmartDefaultVisibility(selectedBasis, selectedSourceFilter));
    setDisplayModes({ ...DEFAULT_DISPLAY_MODES });
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  };

  const filterSummaryLabel =
    filters.timeFilterType === 'day'
      ? `${filters.label} | ${filters.dateFrom} to ${filters.dateTo}`
      : `${filters.label} | ${filters.timeFilterType}: ${filters.selectedPeriods.join(', ') || 'N/A'}`;

  const subtitleText = (() => {
    const payloadLabel = data?.ticket_type ?? selectedTicketType;
    if (filters.timeFilterType === 'day') return `${payloadLabel} payload | ${formatDayRange(filters.dateFrom, filters.dateTo)}`;
    if (filters.timeFilterType === 'week') return `${payloadLabel} payload | ${formatSelectedWeeks(filters.selectedPeriods)}`;
    return `${payloadLabel} payload | ${formatSelectedMonths(filters.selectedPeriods)}`;
  })();

  const handleExcelExport = () => {
    if (!data || loading || error || !hasRows || isExporting) return;
    setIsExporting(true);
    setExportMessage('');

    try {
      const result = exportPlatformSummaryToExcel({
        generatedAt: new Date(),
        selectedTicketType,
        selectedSourceFilter,
        selectedBasis,
        showTicketTypeColumn,
        visibleMetricColumns: visibleColumns,
        rows: tableRows,
        grandTotal: data.grand_total,
        dateFrom: data.date_from,
        dateTo: data.date_to,
        filterSummaryLabel,
        platformLabelByKey: PLATFORM_LABEL_BY_KEY,
        getMetricPresentation,
      });
      setExportMessage(`Exported ${result.rowCount} row(s) to Excel.`);
    } catch {
      setExportMessage('Excel export failed. Please retry.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} gap={2} mb={2}>
        <Typography variant="h6" fontWeight={700}>
          Platform Summary
        </Typography>

        <Stack direction="row" spacing={1.5} flexWrap="wrap" alignItems="center">
          <Tooltip
            title={
              selectedBasis === 'created'
                ? 'Created: every number reflects tickets CREATED in the selected week(s) — the current/default view. A ticket created this week keeps counting here even if it resolves weeks later.'
                : 'Resolved: every number reflects tickets RESOLVED in the selected week(s), regardless of when they were created. This is a different set of tickets than the Created view, not the same tickets viewed differently.'
            }
            arrow
          >
            <ToggleButtonGroup
              size="small"
              exclusive
              value={selectedBasis}
              onChange={(_e, v: AutomationBasis | null) => v && setSelectedBasis(v)}
            >
              <ToggleButton value="created">Created</ToggleButton>
              <ToggleButton value="resolved">Resolved</ToggleButton>
            </ToggleButtonGroup>
          </Tooltip>

          <ToggleButtonGroup
            size="small"
            exclusive
            value={selectedSourceFilter}
            onChange={(_e, v: SourceFilterType | null) => v && setSelectedSourceFilter(v)}
          >
            {PLATFORM_SEGMENTS.map((s) => (
              <ToggleButton key={s.value} value={s.value}>
                {s.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <Button size="small" variant="outlined" onClick={(e) => setSettingsAnchorEl(e.currentTarget)}>
            Table Settings
          </Button>

          <Button size="small" variant="outlined" disabled={loading || !!error || !data || !hasRows || isExporting} onClick={handleExcelExport}>
            {isExporting ? 'Exporting…' : 'Export Excel'}
          </Button>
        </Stack>
      </Stack>

      <Popover
        open={Boolean(settingsAnchorEl)}
        anchorEl={settingsAnchorEl}
        onClose={() => setSettingsAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ width: 300, maxHeight: 460, overflowY: 'auto', p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle2" fontWeight={600}>
              Table Settings
            </Typography>
            <Button size="small" onClick={resetTableSettings}>
              Reset to defaults
            </Button>
          </Stack>

          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            Visible Columns
          </Typography>
          {columnGroups.map((group) => (
            <Box key={group.category} sx={{ mb: 1.5, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption" fontWeight={600} display="block" mb={0.5}>
                {group.label}
              </Typography>
              {group.columns.map((column) => (
                <FormControlLabel
                  key={column.id}
                  sx={{ display: 'flex' }}
                  control={
                    <Checkbox size="small" checked={columnVisibility[column.id]} onChange={(e) => updateColumnVisibility(column.id, e.target.checked)} />
                  }
                  label={<Typography variant="body2">{column.label}</Typography>}
                />
              ))}
            </Box>
          ))}

          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            Metric Display
          </Typography>
          {COLUMNS.filter((c) => c.displayModeConfig).map((column) => (
            <Box key={column.id} sx={{ mb: 1 }}>
              <Typography variant="body2" fontWeight={600} mb={0.5}>
                {column.label}
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                fullWidth
                value={displayModes[column.displayModeConfig!.metric]}
                onChange={(_e, v: DisplayMode | null) => v && updateDisplayMode(column.displayModeConfig!.metric, v)}
              >
                <ToggleButton value="count">Count</ToggleButton>
                <ToggleButton value="percentage">Percentage</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          ))}
        </Box>
      </Popover>

      <ToggleButtonGroup
        size="small"
        exclusive
        value={selectedTicketType}
        onChange={(_e, v: TicketType | null) => v && setSelectedTicketType(v)}
        sx={{ mb: 2 }}
      >
        <ToggleButton value="ALL">ALL</ToggleButton>
        <ToggleButton value="INC">INC</ToggleButton>
        <ToggleButton value="RITM">SR</ToggleButton>
      </ToggleButtonGroup>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {!loading && !error && data ? (
        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
          {subtitleText}
        </Typography>
      ) : null}

      {exportMessage ? (
        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
          {exportMessage}
        </Typography>
      ) : null}

      {(
        <TableContainer sx={{ maxHeight: 560, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ position: 'sticky', left: 0, zIndex: 3, bgcolor: 'background.paper', fontWeight: 700 }}>Platform</TableCell>
                {showTicketTypeColumn ? <TableCell sx={{ fontWeight: 700 }}>Ticket Type</TableCell> : null}
                {visibleColumns.map((c) => (
                  <TableCell key={c.id} align="center" sx={{ fontWeight: 700 }}>
                    {c.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading
                ? Array.from({ length: 8 }).map((_, i) => (
                    <TableRow key={`skeleton-${i}`}>
                      <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper' }}>
                        <Skeleton variant="text" width="80%" />
                      </TableCell>
                      {showTicketTypeColumn ? (
                        <TableCell>
                          <Skeleton variant="text" width="60%" />
                        </TableCell>
                      ) : null}
                      {visibleColumns.map((c) => (
                        <TableCell key={c.id} align="center">
                          <Skeleton variant="text" width="65%" sx={{ mx: 'auto' }} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : !error
                ? tableRows.map((row, index) => {
                    const isExpanded = expandedPlatformKey === row.platform_key;
                    const fullColSpan = 1 + (showTicketTypeColumn ? 1 : 0) + visibleColumns.length;
                    return (
                      <React.Fragment key={`${row.platform_key}-${row.ticket_type}-${index}`}>
                        <TableRow hover selected={isExpanded}>
                          <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper' }}>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <Tooltip title={isExpanded ? 'Hide assignment groups' : 'Show assignment groups'}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleToggleExpand(row.platform_key)}
                                  aria-label={isExpanded ? 'Collapse assignment groups' : 'Expand assignment groups'}
                                >
                                  {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                              <span>{PLATFORM_LABEL_BY_KEY[row.platform_key] ?? row.platform_name}</span>
                            </Stack>
                          </TableCell>
                          {showTicketTypeColumn ? (
                            <TableCell>{TICKET_TYPE_DISPLAY_LABEL[row.ticket_type] ?? row.ticket_type}</TableCell>
                          ) : null}
                          {visibleColumns.map((c) => (
                            <TableCell key={c.id} align="center">
                              {AUTOMATION_CLICKABLE_COLUMNS.has(c.id) && row[c.id] != null && row[c.id] !== 0 ? (
                                <Typography
                                  component="span"
                                  variant="body2"
                                  sx={{ cursor: 'pointer', color: 'inherit', textDecoration: 'underline' }}
                                  onClick={() => openAutomationDrilldown(
                                    row.platform_key,
                                    PLATFORM_LABEL_BY_KEY[row.platform_key] ?? row.platform_name,
                                    c.id, c.label,
                                  )}
                                >
                                  {getMetricText(c.id, row[c.id], row)}
                                </Typography>
                              ) : (
                                getMetricText(c.id, row[c.id], row)
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ p: 0, borderBottom: isExpanded ? undefined : 'none' }} colSpan={fullColSpan}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{ py: 1, px: 2, bgcolor: 'action.hover' }}>
                                {groupsLoading ? (
                                  <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1 }}>
                                    <Skeleton variant="text" width={160} />
                                  </Stack>
                                ) : groupsError ? (
                                  <Alert severity="warning" sx={{ my: 1 }}>
                                    {groupsError}
                                  </Alert>
                                ) : groupRows.length === 0 ? (
                                  <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                                    No assignment groups found for this platform and filter selection.
                                  </Typography>
                                ) : (
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Assignment group</TableCell>
                                        {visibleColumns.map((c) => (
                                          <TableCell key={c.id} align="center" sx={{ fontWeight: 600 }}>
                                            {c.label}
                                          </TableCell>
                                        ))}
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {groupRows.map((groupRow, groupIndex) => {
                                        const isSelected =
                                          scope.platform === row.platform_key && scope.assignmentGroup === groupRow.assignment_group;
                                        return (
                                          <TableRow
                                            key={`${groupRow.assignment_group}-${groupIndex}`}
                                            hover
                                            selected={isSelected}
                                            role="button"
                                            tabIndex={0}
                                            aria-pressed={isSelected}
                                            onClick={() => handleSelectGroup(row.platform_key, groupRow.assignment_group)}
                                            onKeyDown={(event) => {
                                              if (event.key === 'Enter' || event.key === ' ') {
                                                event.preventDefault();
                                                handleSelectGroup(row.platform_key, groupRow.assignment_group);
                                              }
                                            }}
                                            sx={{ cursor: 'pointer' }}
                                          >
                                            <TableCell>{groupRow.assignment_group}</TableCell>
                                            {visibleColumns.map((c) => (
                                              <TableCell key={c.id} align="center">
                                                {AUTOMATION_CLICKABLE_COLUMNS.has(c.id) && groupRow[c.id] != null && groupRow[c.id] !== 0 ? (
                                                  <Typography
                                                    component="span"
                                                    variant="body2"
                                                    sx={{ cursor: 'pointer', color: 'inherit', textDecoration: 'underline' }}
                                                    onClick={() => openAutomationDrilldown(
                                                      row.platform_key,
                                                      PLATFORM_LABEL_BY_KEY[row.platform_key] ?? row.platform_name,
                                                      c.id, c.label,
                                                      groupRow.assignment_group === 'Other Groups' ? '__OTHER__' : groupRow.assignment_group,
                                                    )}
                                                  >
                                                    {getMetricText(c.id, groupRow[c.id], groupRow)}
                                                  </Typography>
                                                ) : (
                                                  getMetricText(c.id, groupRow[c.id], groupRow)
                                                )}
                                              </TableCell>
                                            ))}
                                          </TableRow>
                                        );
                                      })}
                                    </TableBody>
                                  </Table>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })
                : null}

              {!loading && !error && data && tableRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={1 + (showTicketTypeColumn ? 1 : 0) + visibleColumns.length}>
                    <Typography variant="body2" color="text.secondary">
                      No platform rows available for the selected filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}

              {!loading && !error && data?.grand_total ? (
                <TableRow>
                  <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', fontWeight: 700, borderTop: '2px solid', borderColor: 'divider' }}>
                    Grand total
                  </TableCell>
                  {showTicketTypeColumn ? (
                    <TableCell sx={{ fontWeight: 700, borderTop: '2px solid', borderColor: 'divider' }}>ALL</TableCell>
                  ) : null}
                  {visibleColumns.map((c) => (
                    <TableCell key={c.id} align="center" sx={{ fontWeight: 700, borderTop: '2px solid', borderColor: 'divider' }}>
                      {getMetricText(c.id, data.grand_total?.[c.id], data.grand_total as PlatformSummaryMetrics)}
                    </TableCell>
                  ))}
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <TicketDrilldownModal context={drilldownCtx} onClose={() => setDrilldownCtx(null)} />
    </Box>
  );
}
